import {LoginAntwoord, StartAntwoord, WachtwoordAntwoord} from 'common';
import {backend, BackendError, METHOD_POST} from '@/base/callBackend.js';

function isStatusBody(b: unknown): b is {status: string} {
    return typeof b === 'object' && b !== null && typeof (b as {status?: unknown}).status === 'string';
}

/**
 * The auth endpoints answer their refusals with a JSON body too (401/429 for the logins, 400 for wachtwoord-zetten),
 * but backend() turns every non-2xx into a BackendError with the raw text in details: dig that body back out, so the
 * dialogs see one shape whatever the status code was.
 */
function antwoordUitFout<T extends {status: string}>(e: unknown): T | undefined {
    if (!(e instanceof BackendError)) {
        return undefined;
    }
    let body: unknown;
    try {
        body = typeof e.details === 'string' ? JSON.parse(e.details) : e.details;
    } catch (_) {
        body = undefined;
    }
    return isStatusBody(body) ? body as T : undefined;
}

// the first step: password, or a set-password mail was just sent
export async function start(email: string): Promise<StartAntwoord> {
    try {
        return await backend(METHOD_POST, '/api/auth/start', {email});
    } catch (e: unknown) {
        return antwoordUitFout<StartAntwoord>(e) ?? {status: 'wachtwoord'};
    }
}

export async function login(email: string, wachtwoord: string): Promise<LoginAntwoord> {
    try {
        return await backend(METHOD_POST, '/api/auth/login', {email, wachtwoord});
    } catch (e: unknown) {
        return antwoordUitFout<LoginAntwoord>(e) ?? {status: 'fout'};
    }
}

export async function loginCode(email: string, code: string, onthoudBrowser: boolean): Promise<LoginAntwoord> {
    try {
        return await backend(METHOD_POST, '/api/auth/login-code', {email, code, onthoudBrowser});
    } catch (e: unknown) {
        return antwoordUitFout<LoginAntwoord>(e) ?? {status: 'fout'};
    }
}

export async function logout(): Promise<void> {
    await backend(METHOD_POST, '/api/auth/logout', {});
}

export async function wachtwoordVergeten(email: string): Promise<void> {
    try {
        await backend(METHOD_POST, '/api/auth/wachtwoord-vergeten', {email});
    } catch (e: unknown) {
        if (!(e instanceof BackendError)) {
            throw e;
        }
    }
}

// whether a reset or onboarding link is still usable; false on an expired link and on any failure
export async function tokenControle(token: string): Promise<boolean> {
    try {
        const antwoord: WachtwoordAntwoord = await backend(METHOD_POST, '/api/auth/token-controle', {token});
        return antwoord.status === 'ok';
    } catch (e: unknown) {
        return false;
    }
}

export async function wachtwoordZetten(token: string, wachtwoord: string): Promise<WachtwoordAntwoord> {
    try {
        return await backend(METHOD_POST, '/api/auth/wachtwoord-zetten', {token, wachtwoord});
    } catch (e: unknown) {
        return antwoordUitFout<WachtwoordAntwoord>(e) ?? {status: 'fout'};
    }
}
